import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { Camera, Trash2, Edit2, Plus, LogOut, ExternalLink, Save, X, Image as ImageIcon, AlertCircle, Users } from "lucide-react";
import { Section, SectionTitle, TeamLogo } from "../components/UIBits";
import { useT } from "../lib/i18n";
import teamApi from "../lib/teamApi";

// Client-side image compression to keep base64 small (~500 KB max).
async function compressImageFile(file, maxDim = 800, quality = 0.8) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("read fail"));
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error("decode fail"));
            img.onload = () => {
                let { width, height } = img;
                if (width > height && width > maxDim) {
                    height = Math.round((height * maxDim) / width); width = maxDim;
                } else if (height > maxDim) {
                    width = Math.round((width * maxDim) / height); height = maxDim;
                }
                const canvas = document.createElement("canvas");
                canvas.width = width; canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/jpeg", quality));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });
}

const EMPTY_FORM = { id: null, number: "", first_name: "", last_name: "", dob: "", position: "", nbhpa_url: "", photo_base64: "" };

export default function TeamDashboardPage() {
    const { t } = useT();
    const [team, setTeam] = useState(null);
    const [account, setAccount] = useState(null);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [authed, setAuthed] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);

    const reload = async () => {
        try {
            const me = await teamApi.get("/team/me");
            setTeam(me.data.team);
            setAccount(me.data.account);
            const ps = await teamApi.get("/team/players");
            setPlayers(ps.data);
            setLoading(false);
        } catch (e) {
            if (e?.response?.status === 401) setAuthed(false);
            setLoading(false);
        }
    };
    useEffect(() => { reload(); }, []);

    const logout = () => {
        localStorage.removeItem("du_team_token");
        setAuthed(false);
    };

    const onPickPhoto = async (file) => {
        if (!file) return;
        try {
            const b64 = await compressImageFile(file, 800, 0.82);
            setForm((f) => ({ ...f, photo_base64: b64 }));
        } catch (e) {
            setErr(t("team_dash.photo_err"));
        }
    };

    const startAdd = () => { setForm(EMPTY_FORM); setEditing(false); setErr(""); };
    const startEdit = (p) => {
        setForm({
            id: p.id,
            number: p.number ?? "",
            first_name: p.first_name || p.name?.split(" ")[0] || "",
            last_name: p.last_name || p.name?.split(" ").slice(1).join(" ") || "",
            dob: p.dob || "",
            position: p.position || "",
            nbhpa_url: p.nbhpa_url || "",
            photo_base64: p.photo_base64 || "",
        });
        setEditing(true); setErr("");
    };

    const submit = async (e) => {
        e?.preventDefault();
        setErr(""); setBusy(true);
        const payload = {
            number: Number(form.number),
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            dob: form.dob,
            position: form.position.trim(),
            nbhpa_url: form.nbhpa_url.trim(),
            photo_base64: form.photo_base64 || "",
        };
        try {
            if (editing && form.id) {
                await teamApi.put(`/team/players/${form.id}`, payload);
            } else {
                await teamApi.post(`/team/players`, payload);
            }
            setForm(EMPTY_FORM); setEditing(false);
            await reload();
        } catch (ex) {
            const d = ex?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (Array.isArray(d) ? d.map(x => x.msg).join(", ") : t("team_dash.save_err")));
        } finally {
            setBusy(false);
        }
    };

    const del = async (p) => {
        if (!window.confirm(t("team_dash.delete_confirm", { name: p.name || `${p.first_name} ${p.last_name}` }))) return;
        await teamApi.delete(`/team/players/${p.id}`);
        await reload();
    };

    if (!authed) return <Navigate to="/equipe/login" replace />;
    if (loading) return <Section><div className="text-white/50">{t("common.loading")}</div></Section>;
    if (!team) return <Section><div className="text-white/50">{t("team_dash.no_team")}</div></Section>;

    const atLimit = players.length >= 10 && !editing;

    return (
        <div data-testid="team-dashboard">
            <Helmet><title>{`${team.name} — ${t("team_dash.title")}`}</title></Helmet>
            <Section>
                <div className="flex items-start justify-between gap-4 mb-8 pb-6 border-b border-white/10">
                    <div className="flex items-center gap-4">
                        <TeamLogo team={team} size={64} />
                        <div>
                            <div className="label-mini">{team.category} · {team.city}</div>
                            <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight" style={{ color: team.color || "#7FD93C" }}>{team.name}</h1>
                            <div className="text-sm text-white/60 mt-1">{t("team_dash.manager")}: {account?.manager_name}</div>
                        </div>
                    </div>
                    <button onClick={logout} className="btn-ghost" data-testid="btn-team-logout"><LogOut size={14} /> {t("team_dash.logout")}</button>
                </div>

                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Roster list */}
                    <div className="lg:col-span-3">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-2xl uppercase tracking-widest text-[#4FE0D5] flex items-center gap-2"><Users size={20} /> {t("team_dash.roster")} ({players.length}/10)</h2>
                            <button onClick={startAdd} className="btn-ghost text-[#7FD93C] border-[#7FD93C]/30 hover:border-[#7FD93C]" disabled={atLimit} data-testid="btn-add-player"><Plus size={14} /> {t("team_dash.add_player")}</button>
                        </div>
                        {players.length === 0 && (
                            <div className="text-white/50 text-sm border border-dashed border-white/15 p-8 text-center">{t("team_dash.empty_roster")}</div>
                        )}
                        <div className="grid gap-3">
                            {players.map(p => (
                                <div key={p.id} className="glass-card p-4 flex items-center gap-3" data-testid={`team-player-${p.id}`}>
                                    <div className="font-display text-4xl text-[#7FD93C] w-14 text-center">#{p.number}</div>
                                    {p.photo_base64
                                        ? <img src={p.photo_base64} alt={p.name} className="w-14 h-14 object-cover border border-white/15" />
                                        : <div className="w-14 h-14 flex items-center justify-center border border-white/10 text-white/30"><ImageIcon size={20} /></div>}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold truncate">{p.first_name || p.name?.split(" ")[0]} {p.last_name || p.name?.split(" ").slice(1).join(" ")}</div>
                                        <div className="text-xs text-white/50 flex flex-wrap gap-x-2">
                                            <span>{t("team_dash.dob_short")}: {p.dob || "—"}</span>
                                            {p.position && <span>· {p.position}</span>}
                                            {p.nbhpa_url && <a href={p.nbhpa_url} target="_blank" rel="noreferrer" className="text-[#4FE0D5] hover:text-[#7FD93C] inline-flex items-center gap-1">NBHPA <ExternalLink size={10} /></a>}
                                        </div>
                                    </div>
                                    <button onClick={() => startEdit(p)} className="btn-ghost"><Edit2 size={12} /></button>
                                    <button onClick={() => del(p)} className="btn-ghost text-rose-400 border-rose-400/30"><Trash2 size={12} /></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={submit} className="glass-card p-5 border-l-4 border-[#4FE0D5] sticky top-24" data-testid="team-player-form">
                            <h3 className="font-display text-xl uppercase tracking-widest text-[#4FE0D5] mb-4">{editing ? t("team_dash.edit_player") : t("team_dash.new_player")}</h3>

                            {atLimit && !editing && (
                                <div className="flex items-start gap-2 bg-rose-500/10 border-l-2 border-rose-500 p-3 mb-3 text-xs">
                                    <AlertCircle size={14} className="text-rose-400 mt-0.5" />
                                    <div>{t("team_dash.limit_reached")}</div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2 sm:col-span-1">
                                    <label className="label-mini">{t("team_dash.number")} *</label>
                                    <input type="number" min="0" max="99" required className="input-field" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} data-testid="inp-player-number" />
                                </div>
                                <div className="col-span-2 sm:col-span-1">
                                    <label className="label-mini">{t("team_dash.position")}</label>
                                    <input className="input-field" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder={t("team_dash.position_ph")} />
                                </div>
                                <div className="col-span-2 sm:col-span-1">
                                    <label className="label-mini">{t("team_dash.first_name")} *</label>
                                    <input required className="input-field" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="inp-player-first" />
                                </div>
                                <div className="col-span-2 sm:col-span-1">
                                    <label className="label-mini">{t("team_dash.last_name")} *</label>
                                    <input required className="input-field" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="inp-player-last" />
                                </div>
                                <div className="col-span-2">
                                    <label className="label-mini">{t("team_dash.dob")} * <span className="text-white/40 font-normal">({t("team_dash.dob_format")})</span></label>
                                    <input type="date" required className="input-field" value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} data-testid="inp-player-dob" />
                                </div>
                                <div className="col-span-2">
                                    <label className="label-mini">{t("team_dash.nbhpa")} <span className="text-white/40 font-normal">(www.nbhpa.com/...)</span></label>
                                    <input type="url" className="input-field" value={form.nbhpa_url} onChange={e => setForm({ ...form, nbhpa_url: e.target.value })} placeholder="https://www.nbhpa.com/passport/..." data-testid="inp-player-nbhpa" />
                                </div>
                                <div className="col-span-2">
                                    <label className="label-mini">{t("team_dash.photo")}</label>
                                    <div className="flex items-center gap-3">
                                        {form.photo_base64
                                            ? <img src={form.photo_base64} alt="" className="w-16 h-16 object-cover border border-white/15" />
                                            : <div className="w-16 h-16 flex items-center justify-center border border-white/10 text-white/30"><ImageIcon size={22} /></div>}
                                        <div className="flex flex-col gap-2 flex-1">
                                            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => onPickPhoto(e.target.files?.[0])} data-testid="inp-player-camera" />
                                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => onPickPhoto(e.target.files?.[0])} data-testid="inp-player-file" />
                                            <button type="button" onClick={() => cameraRef.current?.click()} className="btn-ghost text-xs"><Camera size={12} /> {t("team_dash.take_photo")}</button>
                                            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost text-xs"><ImageIcon size={12} /> {t("team_dash.choose_file")}</button>
                                            {form.photo_base64 && <button type="button" onClick={() => setForm({ ...form, photo_base64: "" })} className="text-xs text-rose-400 self-start">{t("team_dash.remove_photo")}</button>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {err && <div className="text-rose-400 text-sm mt-3" data-testid="team-form-err">{err}</div>}

                            <div className="flex gap-2 mt-5">
                                <button type="submit" disabled={busy || atLimit} className="btn-primary flex-1 justify-center" data-testid="btn-save-player"><Save size={14} /> {busy ? t("team_dash.saving") : (editing ? t("common.save") : t("team_dash.add"))}</button>
                                {editing && <button type="button" onClick={startAdd} className="btn-ghost"><X size={14} /> {t("common.cancel")}</button>}
                            </div>
                        </form>
                    </div>
                </div>
            </Section>
        </div>
    );
}
